---
title: Changes
lede: Review what the session changed, leave notes for the agent, and choose what the merge takes.
order: 25
part: Review and merge
---

The Changes surface (`⌘3`) shows everything the session changed, per repository, as one diff you read like a pull request. Nothing reaches your own repositories until you [merge](/merging).

![The changed files](media/changes-list.webp)

### The file list

The column on the left lists changed files by repository, coloured by what happened: blue modified, green added, red deleted. A conflicted file is marked `!`. The band above it reads *ticked / changed*, and how many files you have viewed.

**The tick boxes decide the merge.** A ticked file goes with the merge; an unticked one stays in the session. The box on a repository's row ticks or unticks all its files.

Click a file to scroll the diff to it. Right-click it, or press `⇧F10`, for **Roll back**, which drops the session's changes to that file. Nothing brings them back.

### Reading the diff

![The diff](media/changes-diff.webp)

Every changed file is a block on one page, in the list's order. `↑` and `↓` scroll through a block and on into the next.

A block's header holds the path, the lines added and removed, and these controls:

| Control | What it does |
|---|---|
| Chevron | Folds the block, without marking it viewed |
| Columns icon | Shows the file side by side instead of inline |
| **Viewed** | Marks the file read and folds it |
| Note bubble | A note on the whole file |
| `⋯` | **Open in Files** and **Roll back** |

Once the [Change Map](/change-map) is drawn, the header also names the part the file belongs to; click it to go back to that part on the map.

Unchanged stretches are collapsed: a gap's arrows show a little more, and **Expand all** shows the whole stretch. A conflicted file shows the merge view instead — see [Sync and conflicts](/sync).

### Viewed

**Viewed** tracks what you have read, not what the merge takes. Ticking it folds the block, so what is left on screen is what is left to read.

If the agent changes the file again, or a sync moves its lines, the mark clears by itself. **Mark all viewed** in the toolbar marks every file at once.

### Notes for the agent

Click a line number, or select lines and press **Annotate**, to write what is wrong there. Click the coloured words of a note, or its line number, to open it again. The note bubble in a block's header writes one about the whole file.

**Add** keeps the note to send later. Open it again and **Send** types it into the agent's terminal now, quoting the file and lines. **Send to Agent** in the toolbar sends every note not yet sent, in one message.

### The toolbar

![The review toolbar](media/changes-toolbar.webp)

| Button | What it does |
|---|---|
| **Diff** / **Map** | Switch between the diff and the [Change Map](#the-change-map) |
| **Refresh diff** | Reads the diff again, while the agent is still working |
| **Resolve with Agent** | Shown while files are in conflict: asks the agent to resolve every one |
| **Mark all viewed** | Marks and folds every file |
| **Send to Agent** | Shown with notes to send: sends your unsent notes |

On **Map**, **Find a part** and **Filters** take the place of **Refresh diff** and **Mark all viewed**. While the agent works, the bar reads **Read-only** and **Roll back** is refused.

### The review footer

![The review footer](media/changes-footer.webp)

The footer under the file list says where the session stands against your computer, and offers the one next step.

| Tag | Button |
|---|---|
| **Ready** | **Merge `n` files** — see [Merging](/merging) |
| **Branch moved** | **Sync** — see [Sync and conflicts](/sync) |
| **Sync stopped** | **Sync again**, or **Abandon** |
| **Conflicts with `<base>`** | **Update from `<base>`** — see [Pull requests](/pull-requests) |
| **Merging `<base>`**, **Ready to finish** | **Finish update**, or **Abandon** |

With nothing ticked, **Merge** stays disabled and says why. Where your team has [Rules](/rules), a row above the footer says how the change stands against them. Under the footer, the **Push** block lists what merged and where it can be pushed.

### The Change Map

**Map** draws the parts of the system this change touches, so you can judge the design before reading the diff. See [The Change Map](/change-map).

### After a merge

![The merge receipt](media/changes-merged.webp)

Once everything has merged, the surface shows a receipt: *Merged into `<branch>`*, then **Pushed** and **Pull request** where there is a remote, then the ticket the task came from. Files landed without git read *Landed on* instead.

**Ask Agent** goes back to the agent: new changes come back here and merge onto the same branch. **Close session** destroys the sandbox and files the task in the [archive](/archive); what landed stays.
